'use client';

import { describeIndustrialCaseClassification } from '@/domain/industrialCase/industrialCaseSummary';
import Image from 'next/image';
import type { CSSProperties } from 'react';
import { useEffect, useRef } from 'react';

import * as S from './IndustrialCaseDetailPage.styles';
import type { IndustrialCaseImage } from './industrialCaseData';

type Props = {
  customerName: string;
  image: IndustrialCaseImage;
  meta: string;
  /**
   * Industry, process, and inspection target, shown under the customer name.
   * Any of them may be empty on a case authored before these fields existed.
   */
  classification: {
    industry: string;
    process: string;
    inspectionTarget: string;
  };
};

const MAX_PARALLAX_OFFSET = 150;
const START_SCALE = 1.08;
const MAX_SCALE_REDUCTION = 0.08;
const BASE_BLUR = 1.6;
const MAX_BLUR_INCREASE = 0.8;

type ParallaxStyle = CSSProperties & {
  '--case-parallax-blur': string;
  '--case-parallax-offset': string;
  '--case-parallax-scale': string;
};

const INITIAL_PARALLAX_STYLE: ParallaxStyle = {
  '--case-parallax-blur': `${BASE_BLUR}px`,
  '--case-parallax-offset': '0px',
  '--case-parallax-scale': String(START_SCALE),
};

export default function IndustrialCaseParallaxHero({
  customerName,
  image,
  meta,
  classification,
}: Props) {
  const classificationParts =
    describeIndustrialCaseClassification(classification);
  const stageRef = useRef<HTMLDivElement>(null);
  const imageMotionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const reducedMotionQuery = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    );
    let animationFrame: number | null = null;

    const updatePosition = () => {
      animationFrame = null;

      const stage = stageRef.current;
      const imageMotion = imageMotionRef.current;
      if (!stage || !imageMotion) return;

      const scrollDistance = Math.max(stage.getBoundingClientRect().height, 1);
      const progress = reducedMotionQuery.matches
        ? 0
        : Math.min(Math.max(window.scrollY / scrollDistance, 0), 1);
      const offset = progress * MAX_PARALLAX_OFFSET;
      const scale = START_SCALE - progress * MAX_SCALE_REDUCTION;
      const blur = BASE_BLUR + progress * MAX_BLUR_INCREASE;

      imageMotion.style.setProperty('--case-parallax-offset', `${offset}px`);
      imageMotion.style.setProperty('--case-parallax-scale', String(scale));
      imageMotion.style.setProperty('--case-parallax-blur', `${blur}px`);
    };

    const requestPositionUpdate = () => {
      if (animationFrame !== null) return;
      animationFrame = window.requestAnimationFrame(updatePosition);
    };

    updatePosition();
    window.addEventListener('scroll', requestPositionUpdate, { passive: true });
    window.addEventListener('resize', requestPositionUpdate);
    reducedMotionQuery.addEventListener('change', requestPositionUpdate);

    return () => {
      window.removeEventListener('scroll', requestPositionUpdate);
      window.removeEventListener('resize', requestPositionUpdate);
      reducedMotionQuery.removeEventListener('change', requestPositionUpdate);
      if (animationFrame !== null) window.cancelAnimationFrame(animationFrame);
    };
  }, []);

  return (
    <div
      ref={stageRef}
      className={S.customerStage}
      data-industrial-case-parallax-stage
    >
      <div className={S.customerMedia} aria-hidden="true">
        <div
          ref={imageMotionRef}
          className={S.customerImageMotion}
          data-industrial-case-parallax-image
          style={INITIAL_PARALLAX_STYLE}
        >
          {image.pc && (
            <Image
              className={S.customerImage}
              src={image.pc}
              alt=""
              fill
              sizes="100vw"
              priority
              draggable={false}
            />
          )}
        </div>
      </div>

      <div className={S.customerImageTint} aria-hidden="true" />

      <div className={S.customerIdentity}>
        <strong className={S.customerName}>{customerName}</strong>
        <span className={S.customerMeta}>{meta}</span>
        {classificationParts.length > 0 && (
          <p className={S.classificationList}>
            {classificationParts.map((part, index) => (
              <span key={part}>
                {index > 0 && (
                  <span
                    className={S.classificationSeparator}
                    aria-hidden="true"
                  >
                    {' · '}
                  </span>
                )}
                {part}
              </span>
            ))}
          </p>
        )}
      </div>
    </div>
  );
}
